import React, { useEffect, useRef, useCallback, useState, forwardRef, RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ActiveChatMessages.module.css';
import { Message } from '../message';
import { regenerateIcon } from '../../assets/icons';
import { MessageType } from '../../types/message';

interface ActiveChatMessagesProps {
  messages: MessageType[];
  chat: {
    newChatFromMessage: (messageId: string) => Promise<{ id: string }>;
    icon?: string;
    display_name?: string;
  };
  showPlayablesPopup?: boolean;
  isOverlay?: boolean;
  onRegenerate: (messageId: string) => void;
  onRewind: (messageId: string) => void;
  onEdit: (messageId: string, content: string) => Promise<void>;
  regeneratingMessageId: string | null;
  isPendingAiResponse?: boolean;
  onAvatarClick?: (e: React.MouseEvent) => void;
  hideRegenerateButton?: boolean;
  isMobile?: boolean;
  regenerationHistory?: {
    messageId: string;
    content: string;
    regenerationNumber: number;
  }[];
  selectedRegenerationIndices?: Record<string, number>;
  onSelectRegeneration?: (messageId: string, index: number) => void;
}

const ActiveChatMessages = forwardRef<HTMLDivElement, ActiveChatMessagesProps>(({ 
  messages, 
  chat, 
  showPlayablesPopup, 
  isOverlay = false, 
  onRegenerate, 
  onRewind,
  onEdit,
  regeneratingMessageId,
  isPendingAiResponse,
  onAvatarClick,
  hideRegenerateButton = false,
  isMobile = false,
  regenerationHistory = [],
  selectedRegenerationIndices = {},
  onSelectRegeneration
}, ref) => {
  const navigate = useNavigate();
  const internalMessagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  
  // Use the provided ref if available, otherwise use the internal ref
  const messagesContainerRef = ref as RefObject<HTMLDivElement> || internalMessagesContainerRef;
  
  // Function to check if scrolled to bottom
  const checkScrollPosition = useCallback(() => {
    const container = messagesContainerRef?.current;
    if (!container) return;
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 10;
    setIsScrolledToBottom(isAtBottom);
  }, [messagesContainerRef]);
  
  // Scroll to bottom function with partial scrolling for long messages
  const scrollToBottom = useCallback(() => {
    const container = messagesContainerRef?.current;
    if (!container) return;
    
    // Find the last message element
    const messageElements = container.querySelectorAll(`.${styles.messageListContent} > *`);
    const lastMessageElement = messageElements.length > 0 ? messageElements[messageElements.length - 1] : null;
    
    // Check if we're regenerating a message
    const isRegenerating = regeneratingMessageId !== null;
    
    // If regenerating, don't scroll to avoid jumpy behavior
    if (isRegenerating) return;
    
    if (lastMessageElement) {
      // Get the height of the last message and container
      const messageHeight = (lastMessageElement as HTMLElement).offsetHeight;
      const containerHeight = container.clientHeight;
      
      // If message is very long (e.g., more than 70% of container height) AND not on mobile
      if (messageHeight > containerHeight * 0.7 && !isMobile) {
        // Desktop behavior for long messages: Scroll to show the beginning of the message
        lastMessageElement.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        // For normal sized messages, or all messages on mobile, use the original scroll behavior
        if (isMobile) {
          // Mobile behavior: always scroll the container to the bottom
          container.scrollTop = container.scrollHeight;
        } else {
          // Desktop behavior: use scrollIntoView
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
      }
    } else {
      // Fallback to original behavior if no message element found
      if (isMobile) {
        container.scrollTop = container.scrollHeight;
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [isMobile, regeneratingMessageId]);

  // Scroll on new messages
  useEffect(() => {
    // Don't auto-scroll if we're regenerating a message
    if (regeneratingMessageId !== null) return;
    
    if (isMobile) {
      // Mobile: only scroll if already at bottom
      if (isScrolledToBottom) {
        scrollToBottom();
      }
    } else {
      // Desktop: always scroll to bottom on new messages unless regenerating
      scrollToBottom();
    }
  }, [messages, scrollToBottom, isScrolledToBottom, isMobile, regeneratingMessageId]);
  
  // Add scroll event listener for mobile
  useEffect(() => {
    if (isMobile) {
      const container = messagesContainerRef?.current;
      if (container) {
        container.addEventListener('scroll', checkScrollPosition);
        return () => container.removeEventListener('scroll', checkScrollPosition);
      }
    }
  }, [checkScrollPosition, isMobile, messagesContainerRef]);
  
  // Initial scroll to bottom - but not during regeneration
  useEffect(() => {
    if (regeneratingMessageId === null) {
      scrollToBottom();
    }
  }, [scrollToBottom, regeneratingMessageId]);

  const handleNewChat = async (messageId: string) => {
    try {
      const newChat = await chat.newChatFromMessage(messageId);
      navigate(`/chats/${newChat.id}`);
    } catch (err) {
      console.error('Failed to create new chat:', err);
    }
  };

  const handleSelectRegeneration = (messageId: string, index: number) => {
    if (onSelectRegeneration) {
      onSelectRegeneration(messageId, index);
    }
  };

  return (
    <div className={`${styles.messagesContainer} ${isOverlay ? styles.overlay : ''}`}>
      <div className={styles.messageList} ref={messagesContainerRef}>
        <div className={styles.messageListContent}>
          {messages.map((msg, index) => {
            // Filter regeneration history for this message
            const messageRegenerations = regenerationHistory.filter(regen => regen.messageId === msg.id);
            const currentRegenerationIndex = selectedRegenerationIndices[msg.id] || 0;
            
            // Find the last character message in the chat
            const lastCharacterMessage = [...messages].reverse().find(m => m.member_type === 'character');
            const isLastCharacterMessage = msg.id === lastCharacterMessage?.id;
            
            return (
              <Message 
                key={msg.id}
                message={msg} 
                isOverlay={isOverlay}
                isLoading={msg.id === regeneratingMessageId}
                isLastMessage={index === messages.length - 1}
                isLastCharacterMessage={isLastCharacterMessage}
                onRegenerate={() => onRegenerate(msg.id)}
                onNewChat={() => handleNewChat(msg.id)}
                onRewind={() => onRewind(msg.id)}
                onEdit={async (content) => {
                  try {
                    await onEdit(msg.id, content);
                  } catch (err) {
                    console.error('Failed to edit message:', err);
                  }
                }}
                onAvatarClick={onAvatarClick}
                onContentLoad={scrollToBottom}
                regenerationHistory={messageRegenerations.length > 0 ? messageRegenerations : undefined}
                currentRegenerationIndex={currentRegenerationIndex}
                onSelectRegeneration={(index) => handleSelectRegeneration(msg.id, index)}
              />
            );
          })}
          {isPendingAiResponse && (
            <Message 
              message={{
                id: `pending:${Date.now()}`,
                member_type: 'character',
                icon: chat?.icon,
                display_name: chat?.display_name,
                content: '',
                content_type: 'message'
              }}
              isLoading={true}
            />
          )}
          {/* Add a visible spacer at the bottom */}
          <div className={styles.bottomSpacer}></div>
          {/* Only show this on desktop */}
          {!isMobile && <div ref={messagesEndRef} />}
        </div>
        
        {/* Floating regenerate button - relocated inside messageList for better positioning */}
        {!hideRegenerateButton && messages.length >= 2 && messages[messages.length - 1].member_type === 'character' && (
          <div 
            className={styles.regenerateContainer}
            onTouchMove={(e) => {
              // Only stop propagation to prevent global scrolling
              e.stopPropagation();
            }}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <img 
              src={regenerateIcon} 
              alt="Regenerate" 
              className={`${styles.regenerateIcon} ${regeneratingMessageId !== null ? styles.disabled : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                // Only allow regeneration if no message is currently being regenerated
                if (regeneratingMessageId === null) {
                  onRegenerate(messages[messages.length - 1].id);
                }
              }}
              onTouchMove={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    </div>
  );
});

export default ActiveChatMessages; 